import { OptionsOrGroups } from 'react-select';
import {
  GroupBase,
  MultiValue,
  SingleValue,
} from 'react-select/dist/declarations/src/types';

import { isOptionType } from '@savgroup-front-common/types';

const getOption = <Option, Group extends GroupBase<Option>>(
  options: OptionsOrGroups<Option, Group>,
  value: string | boolean,
): SingleValue<Option> => {
  if (Array.isArray(options)) {
    const filteredOptions = options.reduce<Option[]>((acc, option) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return [...acc, option, ...(option?.options || [])];
    }, []);

    return (
      filteredOptions.find((item) => {
        if (item && isOptionType(item)) {
          return item.value === value;
        }

        return false;
      }) || null
    );
  }

  return null;
};

const getSingleValue = <
  Option extends { value?: string | boolean },
  Group extends GroupBase<Option>,
>(
  value: Option | string,
  options: OptionsOrGroups<Option, Group>,
): SingleValue<Option> => {
  if (typeof value === 'string') {
    return getOption(options, value);
  }

  if (value && value.value) {
    return getOption(options, value.value);
  }

  return value;
};

export const getSelectedOption = <
  Option extends { value?: string | boolean },
  Group extends GroupBase<Option>,
>(
  value: MultiValue<Option> | SingleValue<Option>,
  options: OptionsOrGroups<Option, Group>,
): MultiValue<Option> | SingleValue<Option> => {
  if (!value) {
    return null;
  }

  if (value && Array.isArray(value)) {
    const selectedOptions = value
      .map((v) => getSingleValue(v, options))
      .filter((option) => !!option);

    return selectedOptions as MultiValue<Option>;
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return getSingleValue(value, options);
};

export const sortOptions = <Option extends Record<any, any>>(
  options: readonly Option[],
): Option[] =>
  options.slice().sort((a: Option, b: Option) => {
    if (a.label === undefined || b.label === undefined) {
      return 0;
    }

    if (a.label > b.label) {
      return 1;
    }

    if (b.label > a.label) {
      return -1;
    }

    return 0;
  });
